import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Copy, Download, RefreshCw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const formatCurrency = (valor) =>
  (valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

const statusBadge = (boleto) => {
  if (boleto.cobranca_pago) {
    return (
      <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-50 text-green-700">
        Pago
      </span>
    );
  }
  if (boleto.pix?.qrCode && boleto.pix?.expiracao && new Date(boleto.pix.expiracao) > new Date()) {
    return (
      <span className="px-3 py-1 text-xs font-semibold rounded-full bg-amber-50 text-amber-700">
        PIX disponível
      </span>
    );
  }
  return (
    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-red-50 text-red-700">
      Em aberto
    </span>
  );
};

const PixModal = ({ pix, cobranca, onClose }) => {
  if (!pix || !cobranca) return null;

  const expiracao = pix.expiracao ? new Date(pix.expiracao) : null;

  const copiarCopiaCola = async () => {
    if (!pix.copiaCola) return;
    try {
      await navigator.clipboard.writeText(pix.copiaCola);
      toast.success('Código PIX copiado para a área de transferência.');
    } catch (error) {
      console.error('Erro ao copiar PIX:', error);
      toast.error('Não foi possível copiar. Copie manualmente.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full relative p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-900"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="text-center space-y-2 mb-6">
          <h2 className="text-xl font-semibold text-gray-900">QR Code PIX</h2>
          <p className="text-sm text-gray-500">
            Referente ao mês {cobranca.cobranca_mes_referencia}
          </p>
          <p className="text-lg font-semibold text-primary-600">
            {formatCurrency(cobranca.cobranca_valor_atualizado || cobranca.cobranca_valor_original)}
          </p>
          {expiracao && (
            <p className="text-xs text-amber-600">
              Expira em{' '}
              {expiracao.toLocaleString('pt-BR', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          )}
        </div>

        {pix.qrCode ? (
          <div className="flex justify-center mb-6">
            <img
              src={pix.qrCode}
              alt="QR Code PIX"
              className="w-64 h-64 rounded-lg border border-gray-100 shadow-inner"
            />
          </div>
        ) : (
          <div className="text-center text-sm text-gray-500 mb-6">
            QR Code indisponível. Utilize o código copia e cola abaixo.
          </div>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">PIX Copia e Cola</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <textarea
              className="w-full rounded-xl border-gray-200 focus:border-primary-500 focus:ring-primary-500 text-xs"
              rows={3}
              readOnly
              value={pix.copiaCola || 'Código indisponível.'}
            />
            <button
              onClick={copiarCopiaCola}
              disabled={!pix.copiaCola}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition disabled:opacity-50"
            >
              <Copy className="h-4 w-4" />
              Copiar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MeusBoletos = () => {
  const [boletos, setBoletos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [gerandoPixId, setGerandoPixId] = useState(null);
  const [pixModal, setPixModal] = useState(null);

  const carregarBoletos = async () => {
    try {
      setCarregando(true);
      const response = await api.get('/meus-boletos');
      setBoletos(response.data?.data?.cobrancas || []);
    } catch (error) {
      console.error('Erro ao carregar boletos:', error);
      toast.error(
        error.response?.data?.message || 'Não foi possível carregar seus boletos.'
      );
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarBoletos();
  }, []);

  const gerarPix = async (cobrancaId) => {
    try {
      setGerandoPixId(cobrancaId);
      const response = await api.post(`/cobrancas-condominio/${cobrancaId}/gerar-pix`);
      const cobrancaAtualizada = response.data?.data?.cobranca;

      if (cobrancaAtualizada) {
        setBoletos((prev) =>
          prev.map((boleto) =>
            boleto.cobranca_id === cobrancaAtualizada.cobranca_id ? cobrancaAtualizada : boleto
          )
        );
        setPixModal({
          cobranca: cobrancaAtualizada,
          pix: cobrancaAtualizada.pix || {
            gateway: response.data?.data?.pix?.gateway,
            txid: response.data?.data?.pix?.txid,
            copiaCola: response.data?.data?.pix?.payload,
            qrCode: response.data?.data?.pix?.qrCode,
            expiracao: response.data?.data?.pix?.expiracao
          }
        });
      }
    } catch (error) {
      console.error('Erro ao gerar PIX:', error);
      const message =
        error.response?.data?.message || 'Não foi possível gerar o PIX agora.';
      toast.error(message);
    } finally {
      setGerandoPixId(null);
    }
  };

  const resumo = useMemo(() => {
    const totalAberto = boletos
      .filter((boleto) => !boleto.cobranca_pago)
      .reduce((acc, boleto) => acc + (boleto.cobranca_valor_atualizado || boleto.cobranca_valor_original || 0), 0);
    const totalPago = boletos
      .filter((boleto) => boleto.cobranca_pago)
      .reduce((acc, boleto) => acc + (boleto.cobranca_valor_baixa || boleto.cobranca_valor || 0), 0);

    return {
      aberto: totalAberto,
      pago: totalPago
    };
  }, [boletos]);

  if (carregando) {
    return (
      <div className="flex justify-center items-center h-64 bg-white rounded-2xl shadow-sm">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-wrap gap-6 justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Meus boletos</h1>
            <p className="text-gray-500 mt-1">
              Veja todos os boletos do seu apartamento e gere o QR Code PIX para pagamento.
            </p>
          </div>
          <button
            onClick={carregarBoletos}
            className="inline-flex items-center gap-2 rounded-xl border border-primary-200 px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50"
          >
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <p className="text-sm text-gray-500">Total em aberto</p>
            <p className="text-2xl font-semibold text-red-600 mt-2">
              {formatCurrency(resumo.aberto)}
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <p className="text-sm text-gray-500">Total pago</p>
            <p className="text-2xl font-semibold text-green-600 mt-2">
              {formatCurrency(resumo.pago)}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Referência
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Vencimento
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Valor Atual
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {boletos.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                      Nenhum boleto encontrado para o seu apartamento.
                    </td>
                  </tr>
                )}
                {boletos.map((boleto) => (
                  <tr key={boleto.cobranca_id} className="hover:bg-gray-50/60 transition">
                    <td className="px-4 py-4">
                      <div className="text-sm font-semibold text-gray-900">
                        {boleto.cobranca_mes_referencia || '-'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {boleto.apartamento?.apartamento_bloco
                          ? `Bloco ${boleto.apartamento.apartamento_bloco}`
                          : ''}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {formatDate(boleto.cobranca_data_vencimento)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm font-semibold text-gray-900">
                      {formatCurrency(
                        boleto.cobranca_valor_atualizado || boleto.cobranca_valor_original
                      )}
                    </td>
                    <td className="px-4 py-4">{statusBadge(boleto)}</td>
                    <td className="px-4 py-4 text-right">
                      {!boleto.cobranca_pago && (
                        <button
                          onClick={() => gerarPix(boleto.cobranca_id)}
                          disabled={gerandoPixId === boleto.cobranca_id}
                          className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
                        >
                          {gerandoPixId === boleto.cobranca_id ? (
                            <>
                              <LoadingSpinner size="sm" />
                              Gerando...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4" />
                              Gerar PIX
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {pixModal && (
        <PixModal
          pix={pixModal.pix}
          cobranca={pixModal.cobranca}
          onClose={() => setPixModal(null)}
        />
      )}
    </>
  );
};

export default MeusBoletos;


